import { TokenHolderRowData } from "../TokenHoldersTable/types";
import {
    DEFAULT_PIE_CHART_TOOLTIP_PROPS,
    DEFAULT_PIE_PLOT_OPTIONS,
    PieChart,
    PieChartSeries,
} from "~/modules/visualization/components/PieChart";
import { getRandomColor } from "~/utils/getRandomColor";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";
import React from "react";
import { hexToRGB } from "~/utils/hexToRGB";
import ReactDOMServer from "react-dom/server";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { formatCurrencyValue } from "~/utils/functions/formatCurrencyValue";
import { DEFAULT_PIE_CHART_HEIGHT } from "~/modules/visualization/components/PieChart/consts";

// // // //

// Helper function to build a unique ID to identify each PieChartSeries associated with each holder
// NOTE - we used to simply use holder.address, but there's an edge-case where multiple holders
// have the same address across different networks -> thus we construct the ID by concatenating address + network_id
function buildSeriesID(row: TokenHolderRowData): string {
    return `${row.holder.address}-${row.token.network_id}`;
}

function HolderTooltip(props: { data: TokenHolderRowData }) {
    const { data } = props;
    return (
        <div className="p-1">
            <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-semibold text-secondary-foreground">
                    Holder {data.rank}
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                    <ImageLoader
                        src={data.token.network?.icon_url ?? RWA_COIN_ICON}
                        className="h-4 rounded-full"
                        alt={""}
                    />
                    {data.token.name}
                </span>
            </div>
            <div className="text-xl text-foreground tabular-nums mb-1">
                ${formatCurrencyValue(data.holder.holding_token_value_usd)}
            </div>
            {/* NOTE - the tooltip sizes itself to its content (see `style.width`
            below), so the address is what dictates the tooltip's width. `w-max`
            keeps it on a single line up to ~68 characters (long enough for every
            address format we render today) and wraps anything longer instead of
            running off the screen. */}
            <div className="w-max max-w-[min(36rem,80vw)] break-all whitespace-normal text-sm font-mono text-secondary-foreground">
                {data.holder.address}
            </div>
        </div>
    );
}

// // // //

function SmallHoldersTooltip(props: { total: number }) {
    const { total } = props;

    return (
        <div className="p-1">
            <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-semibold text-secondary-foreground">
                    Remaining Holders
                </span>
            </div>
            <div className="text-xl text-foreground tabular-nums mb-1">
                ${formatCurrencyValue(total)}
            </div>
            <div className="w-max max-w-xs whitespace-normal text-sm text-secondary-foreground">
                Value for all addresses not represented in the top 25 holders.
            </div>
        </div>
    );
}

// // // //

const REMAINING_HOLDERS_ID = "remaining_holders";

export function TokenHoldersPieChartLayout(props: {
    rows: TokenHolderRowData[];
    allOthers: TokenHolderRowData[];
    showOthers: boolean;
}) {
    const { rows, allOthers, showOthers } = props;

    const smallHoldersTotal = allOthers.reduce(
        (total, each) => total + each.holder.holding_token_value_usd,
        0,
    );

    const color = rows[0].token.network?.color_hex ?? getRandomColor();

    const tooltipProps = { ...DEFAULT_PIE_CHART_TOOLTIP_PROPS };
    delete tooltipProps.format;

    let series: PieChartSeries[] = rows.map((r) => {
        const rowColor = r.token.network?.color_hex
            ? hexToRGB(r.token.network?.color_hex, 0.75)
            : hexToRGB(getRandomColor(), 0.75);

        return {
            id: buildSeriesID(r),
            name: `Holder ${r.rank}`,
            value: r.holder.holding_token_value_usd,
            sliced: false,
            color: rowColor,
        };
    });

    // Add "Remaining Holders" item
    if (showOthers) {
        series = series.concat([
            {
                id: REMAINING_HOLDERS_ID,
                name: "Remaining Holders",
                value: smallHoldersTotal,
                sliced: true,
                color: hexToRGB(color, 0.3),
            },
        ]);
    }

    return (
        <WindowResizeObserver
            defaultPlaceholderHeight={DEFAULT_PIE_CHART_HEIGHT}
        >
            <PieChart
                height={DEFAULT_PIE_CHART_HEIGHT}
                plotOptions={{
                    ...DEFAULT_PIE_PLOT_OPTIONS,
                    dataLabels: {
                        enabled: true,
                        format:
                            `<div class="style="text-align:center"><span class="fill-slate-400" style="font-size:14px; font-weight:400;">{point.name}</span><br/><span class="fill-slate-700" style="font-size:16px; font-weight:400;">$` +
                            `{point.dataLabelValue}</span></div>`,
                        distance: 20,
                    },
                }}
                tooltip={{
                    ...tooltipProps,
                    // Highcharts caps the tooltip at the chart's width unless a
                    // `width` is set here - "auto" lets it grow to fit the
                    // holder's address instead of clipping it
                    style: { width: "auto" },
                    // The tooltip is now wider than the chart's column, so it
                    // has to render outside of the (clipped) chart container
                    outside: true,
                    formatter: function (this: any) {
                        const point = this.point;

                        // Render tooltip for "Remaining Holders"
                        if (point.summary.id === REMAINING_HOLDERS_ID) {
                            return ReactDOMServer.renderToString(
                                <SmallHoldersTooltip
                                    total={smallHoldersTotal}
                                />,
                            );
                        }

                        // Lookup the holder associated with the point
                        const holder = rows.find(
                            (r) => buildSeriesID(r) === point.summary.id,
                        );

                        // Return N/A if holder can't be found
                        if (!holder) {
                            return "N/A";
                        }

                        // ENHANCEMENT - replace this with a more optimized approach (like string interpolation)
                        return ReactDOMServer.renderToString(
                            <HolderTooltip data={holder} />,
                        );
                    },
                }}
                series={series}
            />
        </WindowResizeObserver>
    );
}

export const TokenHoldersPieChart = React.memo(TokenHoldersPieChartLayout);
